import { useBehavior, useApp, useButton, useTriggerAction } from '@libs/hooks';
import { AssetsSvgMap, ECalculatorErrorType, TBaseCalculatorData, TCalculatorError, TWidget } from '@libs/types';
import { SplitText } from '@libs/utils';
import React from 'react';
import { ButtonCircleView } from '../../Buttons/ButtonCircleView';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { TextCommon } from '../../UI/Text/TextCommon';
import styles from '../Calculator.module.css';
import { sanitiseServerValue } from '../utils/format/format';
import { ActionNameEnum, InputEnum, THandleButtonClick, THandleUpdateValue } from '../utils/types';

type TCalculatorErrorProps = {
    widget: TWidget<TBaseCalculatorData>;
    handleUpdateValue: THandleUpdateValue;
    handleButtonClick?: THandleButtonClick;
    name: InputEnum;
    newInputValue?: string;
    error: TCalculatorError;
    buttonGotItLabel?: string;
    buttonIncreaseLimitLabel?: string;
    buttonChangeAmountLabel?: string;
    buttonChangeDeliveryOptionLabel?: string;
    buttonChangeTransferOptionLabel?: string;
};

const CalculatorError = ({
    widget,
    newInputValue,
    name,
    handleUpdateValue,
    error,
    handleButtonClick,
    buttonGotItLabel = '',
    buttonIncreaseLimitLabel = '',
    buttonChangeAmountLabel = '',
    buttonChangeDeliveryOptionLabel = '',
    buttonChangeTransferOptionLabel = '',
}: TCalculatorErrorProps) => {
    const { setPopup } = useApp();
    const { isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClickByActionName } = useButton(widget, { isDisabled, triggerActionsByType,triggerActionsByNameAndType });
    const isShowButtonCircle = error?.type !== ECalculatorErrorType.MIN_AMOUNT;
    const onChangeAmountClick = () => {
        if (newInputValue) {
            const isFrom = name === InputEnum.FROM_INPUT;
            handleUpdateValue(sanitiseServerValue(newInputValue), name, isFrom);
        }
        setPopup(null);
    };

    const handleClick = (actionName?: ActionNameEnum) => {
        setPopup(null);
        if (actionName) {
            handleClickByActionName(actionName);
        }
    };

    const getErrorButtons = () => {
        const buttons: Array<React.ReactNode> = [];

        if (error?.increaseLimitOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.increaseLimit}
                    label={buttonIncreaseLimitLabel}
                    iconName="ic_arrow_up"
                    onClick={() => handleClick(ActionNameEnum.increaseLimit)}
                />,
            );
        }
        if (error?.changeAmount) {
            buttons.push(
                <ButtonRowView key="changeAmount" label={buttonChangeAmountLabel} iconName="ic_arrow_retry" onClick={onChangeAmountClick} />,
            );
        }
        if (error?.changeDeliveryOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.onChangeDeliveryOptions}
                    label={buttonChangeDeliveryOptionLabel}
                    iconName="ic_arrow_back"
                    onClick={() => handleClick(ActionNameEnum.onChangeDeliveryOptions)}
                />,
            );
        }
        if (error?.withdrawButtonVisible) {
            buttons.push(
                <ButtonRowView
                    key="withdrawButtonVisible"
                    label={`${error?.withdrawButtonText}${error?.maxWithdrawValue} ${error?.withdrawCurIso?.toUpperCase() ?? ''}`}
                    iconName="ic_arrow_forward"
                    onClick={() => {
                        setPopup(null);
                        handleButtonClick?.();
                    }}
                />,
            );
        }
        if (error?.changeTransferOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.onChangeTransferOptions}
                    label={buttonChangeTransferOptionLabel}
                    iconName="ic_arrow_back"
                    onClick={() => handleClick(ActionNameEnum.onChangeTransferOptions)}
                />,
            );
        }
        if (error?.gotIt) {
            buttons.push(<ButtonRowView label={buttonGotItLabel} iconName="ic_check" onClick={() => handleClick()} />);
        }
        return buttons?.map((button, index) => <div key={index}>{button}</div>);
    };

    const handleErrorClick = () => {
        setPopup(<PopupComponent header={<SplitText text={error?.longText ?? ''} />} externalContent={[getErrorButtons()]} />);
    };

    return (
        <span className={styles.errorWrapper}>
            <TextCommon isWidget={false} as="span" content={error?.shortText ?? ''} options={{ variant: 'secondary', textColor: 'pink_100' }} />
            {isShowButtonCircle && (
                <ButtonCircleView
                    size="small"
                    iconName={AssetsSvgMap.ic_info_circle_small}
                    hierarchy="destructive"
                    iconWidth={16}
                    iconHeight={16}
                    onClick={handleErrorClick}
                />
            )}
        </span>
    );
};
export default React.memo(CalculatorError);
